import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, BookOpen, Rocket } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader, Field } from "@/components/app/bits";
import PracticeTemplates from "@/components/app/PracticeTemplates";
import RolloutApplyDialog from "@/components/app/RolloutApplyDialog";

const FRAMEWORKS = ["adkar", "prosci3", "kotter", "lewin", "itil", "comms"];
const RECOVERY = ["relaunch", "stabilise"];
const GROUPS = [
  { title: "Change frameworks", sub: "Pick the lens that fits the change — ADKAR, Prosci, Kotter, Lewin, ITIL.", keys: FRAMEWORKS },
  { title: "Recovery", sub: "For a change in trouble: stabilise it, or relaunch after a failed attempt.", keys: RECOVERY },
];

export default function Templates() {
  const [items, setItems] = useState([]);
  const [variants, setVariants] = useState([]);
  const [sel, setSel] = useState(null);
  const [rollout, setRollout] = useState(null);
  const [f, setF] = useState({ title: "", change_type: "normal", category: "Process" });
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  useEffect(() => {
    api.get("/templates").then((r) => setItems(r.data));
    api.get("/rollout/variants").then((r) => setVariants(r.data.variants)).catch(() => {});
  }, []);
  const apply = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post(`/templates/${sel.key}/apply`, f); toast.success(`${data.ref} created from ${sel.name}`); nav(`/app/changes/${data.id}`); } catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); }
  };
  const byKey = (k) => items.find((t) => t.key === k);

  const Card = (t) => (
    <div key={t.key} data-testid={`template-${t.key}`} className="group pp-card pp-card-hover p-6 flex flex-col">
      <div className="flex items-center justify-between"><span className="pp-pill">{t.family}</span><BookOpen size={16} className="text-faint group-hover:text-accent-600 transition-colors" /></div>
      <h2 className="font-head text-xl font-extrabold mt-4">{t.name}</h2>
      <div className="font-mono text-[11px] text-accent-700 mt-1">{t.tagline}</div>
      <p className="text-sm text-subtle mt-3 leading-relaxed">{t.best_for}</p>
      <ol className="mt-4 space-y-1 text-xs text-subtle">{t.phases.map((p, i) => <li key={p} className="flex gap-2"><span className="font-mono text-muted-foreground w-4">{i + 1}</span>{p}</li>)}</ol>
      <button data-testid={`use-template-${t.key}`} onClick={() => { setSel(t); setF({ title: "", change_type: t.key === "itil" ? "normal" : t.key === "kotter" ? "organizational" : "normal", category: t.key === "itil" ? "Technology" : "Process" }); }} className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-body hover:text-accent-700 transition-colors">Use this playbook <ArrowRight size={14} /></button>
    </div>
  );

  return (
    <div data-testid="templates-page">
      <PageHeader overline="Templates & playbooks" title="Start from a ready-made plan" sub="Each playbook pre-fills a new change so you never start from blank. Grouped by what they're for." />

      {GROUPS.map((g) => (
        <section key={g.title} className="mb-10" data-testid={`template-group-${g.title.toLowerCase().replace(/\s+/g, "-")}`}>
          <h2 className="font-head text-lg font-extrabold">{g.title}</h2>
          <p className="text-sm text-muted-foreground mb-4">{g.sub}</p>
          <div className="grid md:grid-cols-2 gap-5">{g.keys.map(byKey).filter(Boolean).map(Card)}</div>
        </section>
      ))}

      <section className="mb-10" data-testid="template-group-system-rollout">
        <h2 className="font-head text-lg font-extrabold flex items-center gap-2"><Rocket size={18} /> System Rollout</h2>
        <p className="text-sm text-muted-foreground mb-4">People-side plans for a system go-live — the change arc anchored to the project's dates. Each variant seeds comms, training, impacts, measures and go-live conditions you then edit. Product names say what a variant is for; none is vendor-endorsed.</p>
        <div className="grid md:grid-cols-2 gap-5">
          {variants.map((v) => (
            <div key={v.key} data-testid={`rollout-variant-${v.key}`} className="group pp-card pp-card-hover p-6 flex flex-col">
              <div className="flex items-center justify-between"><span className="pp-pill">{v.product || "Generic"}</span><Rocket size={16} className="text-faint group-hover:text-accent-600 transition-colors" /></div>
              <h3 className="font-head text-xl font-extrabold mt-4">{v.name}</h3>
              <div className="font-mono text-[11px] text-accent-700 mt-1">{v.tagline}</div>
              <p className="text-sm text-subtle mt-3 leading-relaxed">{v.best_for}</p>
              <div className="mt-4 flex flex-wrap gap-1.5 text-[11px]" data-testid={`rollout-counts-${v.key}`}>
                {[["comms", "comms"], ["curricula", "curricula"], ["impacts", "impacts"], ["metrics", "measures"], ["conditions", "conditions"], ["gates", "gates"]].map(([k, lbl]) => (
                  <span key={k} className="rounded-full bg-muted px-2 py-0.5 text-muted-foreground"><span className="font-bold text-foreground">{v.creates[k]}</span> {lbl}</span>
                ))}
              </div>
              <div className="mt-2 text-[11px] text-muted-foreground">Starting point · compiled {v.compiled}</div>
              <button data-testid={`use-rollout-${v.key}`} onClick={() => setRollout(v)} className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-body hover:text-accent-700 transition-colors">Preview & apply <ArrowRight size={14} /></button>
            </div>
          ))}
        </div>
      </section>

      <PracticeTemplates />

      <RolloutApplyDialog variant={rollout} open={!!rollout} onClose={() => setRollout(null)} />
      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="rounded-2xl" data-testid="template-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl font-extrabold">New change from {sel?.name}</DialogTitle></DialogHeader>
          <form onSubmit={apply} className="space-y-4 mt-2">
            <Field label="Change title"><input data-testid="tpl-title" className="pp-input" required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="What's the change?" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Type"><select className="pp-input capitalize" value={f.change_type} onChange={(e) => setF({ ...f, change_type: e.target.value })}>{["standard", "normal", "emergency", "organizational"].map((t) => <option key={t}>{t}</option>)}</select></Field>
              <Field label="Category"><select className="pp-input" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>{["Process", "Technology", "People & Culture", "Policy", "Structure"].map((t) => <option key={t}>{t}</option>)}</select></Field>
            </div>
            <div className="flex justify-end"><button data-testid="tpl-submit" disabled={busy} className="pp-btn-dark">{busy ? "Creating…" : "Create change"}</button></div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
